import JDFLogo from '../../assets/svg/Logo.svg';
import InputWidget from '../../lib/InputWidget/InputWidget';
import {postAPICall} from '../../services/apiCallsHandler';
import {
  COUNTRY_CONTRACT_LENGTH,
  COUNTRY_LANGUAGE_CODE,
} from '../../utils/constant';
import Smartlook from '../../utils/smartlook';
import {sendCustomDataNR, sendScreenViewNR} from '../../utils/util';

import {
  Button,
  CardContent,
  CircularProgress,
  TextField,
  useToastProvider,
} from '@deere/fuel-react';
import {Grid} from '@mui/material';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import './ForgotCustomerID.css';

const ForgotCustomerID = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedCountry =
    location.state?.selectedCountry ||
    localStorage.getItem('countryCode') ||
    'gb';
  const returnPath = location.state?.returnPath || '/verification';

  const [firstName, setFirstName] = React.useState('');
  const [firstNameError, setFirstNameError] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [lastNameError, setLastNameError] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [contract, setContract] = React.useState('');
  const [contractErrorMsg, setContractErrorMsg] = React.useState('');
  const [loader, setLoader] = React.useState(false);
  const [verifyEmailDisabled, setVerifyEmailDisabled] = React.useState(true);
  const [submitDisabled, setSubmitDisabled] = React.useState(false);
  const [otpCode, setOtpCode] = React.useState('');
  const [otpSent, setOtpSent] = React.useState(false);
  const [verifyLoader, setVerifyLoader] = React.useState(false);

  const {t} = useTranslation();
  const {enqueueToast} = useToastProvider();

  React.useEffect(() => {
    sendScreenViewNR('GetCustomerId');
    Smartlook.navigation('GetCustomerId');
  }, []);

  const forgotUniqueID = () => {
    navigate(returnPath, {replace: true});
  };

  const clearFormFields = () => {
    setFirstName('');
    setLastName('');
    setContract('');
    setEmail('');
  };

  const handleValue = (value, field) => {
    const setStateAndError = (setState, setError, errorMsg) => {
      setState(value);
      setError(value === '' ? errorMsg : '');
    };

    switch (field) {
      case 'firstName':
        setStateAndError(
          setFirstName,
          setFirstNameError,
          'FIRST_NAME_REQUIRED'
        );
        break;
      case 'lastName':
        setStateAndError(setLastName, setLastNameError, 'LAST_NAME_REQUIRED');
        break;
      case 'email':
        setEmail(value);
        setEmailError(
          !/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value) || value === ''
            ? 'EMAIL_REQUIRED'
            : ''
        );
        setVerifyEmailDisabled(
          !/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value) || value === ''
        );
        break;
    }
  };

  const handleContractValue = (value) => {
    let errorMsg;
    if (value.length === 0) {
      errorMsg = 'CONTRACT_NUMBER_REQUIRED';
    } else if (value.length === COUNTRY_CONTRACT_LENGTH[selectedCountry]) {
      errorMsg = '';
    } else {
      errorMsg = 'CONTRACT_NUMBER_SIX_DIGIT';
    }
    setContractErrorMsg(errorMsg);
    if (/^\d*$/.test(value)) {
      setContract(value);
    }
  };

  const handleSubmit = async () => {
    setLoader(true);
    setSubmitDisabled(true);
    setVerifyEmailDisabled(true);
    sendCustomDataNR(
      'SubmitFetchCustomerID',
      sessionStorage.getItem('trace-id'),
      localStorage.getItem('countryCode'),
      null,
      'SubmitFetchCustomerID_Clicked'
    );
    postAPICall(
      'getcustomer',
      {
        firstName: firstName,
        lastName: lastName,
        contract: contract,
        email: email,
        country: selectedCountry,
        otp: otpCode,
      },
      {
        errorTag: 'FETCH_CUSTOMERID',
        redirectToOops: false,
      }
    ).then((res) => {
      setLoader(false);
      setSubmitDisabled(false);
      setOtpCode('');
      switch (res.status) {
        case 200:
          clearFormFields();
          setOtpSent(false);
          enqueueToast({
            variant: 'success',
            title: t('CUSTOMERID_REQUEST_SUCCESS'),
            isCloseable: true,
          });
          setTimeout(() => {
            forgotUniqueID();
          }, 4500);
          break;
        case 401:
          enqueueToast({
            variant: 'error',
            title: t('OTP_VERIFICATION_FAILED'),
            isCloseable: true,
          });
          break;
        case 500:
          clearFormFields();
          setOtpSent(false);
          enqueueToast({
            variant: 'error',
            title: t('SOMETHING_WENT_WRONG_TRY_AGAIN'),
            isCloseable: true,
          });
          break;
        default:
          clearFormFields();
          setOtpSent(false);
          enqueueToast({
            variant: 'error',
            title: t('CUSTOMERID_REQUEST_FAILED'),
            isCloseable: true,
          });
          break;
      }
    });
  };

  const otpChangeHandler = (e) => {
    const cleanedValue = e.replaceAll(/\D/g, '');
    setOtpCode(cleanedValue);
  };

  const sendOtptoUser = () => {
    setVerifyLoader(true);
    setVerifyEmailDisabled(true);
    postAPICall(
      'otp',
      {
        oktaUserId: email,
        otpPreference: ['email'],
        emailIds: email,
        language: COUNTRY_LANGUAGE_CODE[selectedCountry],
      },
      {
        errorTag: 'CUSTOMERID_OTP',
      }
    ).then((res) => {
      setVerifyLoader(false);
      setVerifyEmailDisabled(false);
      if (res.status == 200) {
        setOtpSent(true);
      } else if (res.status === 429) {
        enqueueToast({
          variant: 'error',
          title: t('OTP_LIMIT'),
          isCloseable: true,
        });
      }
    });
  };

  const formFields = [
    {
      label: 'FIRST_NAME',
      type: 'text',
      required: true,
      value: firstName,
      setValue: (val) => handleValue(val, 'firstName'),
      error: firstNameError !== '',
      helperText: t(firstNameError),
      id: 'firstName',
    },
    {
      label: 'LAST_NAME',
      type: 'text',
      required: true,
      value: lastName,
      setValue: (val) => handleValue(val, 'lastName'),
      error: lastNameError !== '',
      helperText: t(lastNameError),
      id: 'lastName',
      isHidden: false,
    },
    {
      label: 'CONTRACT_NO',
      type: 'text',
      required: true,
      value: contract,
      setValue: handleContractValue,
      error: contractErrorMsg !== '',
      helperText: t(contractErrorMsg),
      id: 'contract',
      isHidden: false,
    },
    {
      label: 'EMAIL_ADDRESS',
      type: 'email',
      required: true,
      value: email,
      setValue: (val) => handleValue(val, 'email'),
      error: emailError !== '',
      helperText: t(emailError),
      id: 'email',
      isHidden: false,
    },
  ];

  return (
    <div className="ForgotCustomerIDContent">
      <div>
        <div className="ForgotCustomerIDHeading">
          <div className="jdf-logo">
            <img src={JDFLogo} alt="JDF Logo" />
          </div>
          <div>
            <h2>{t('GET_UNIQUE_ID')}</h2>
          </div>
          <p id="SubHeading">{t('SECURITY_VERIFICATION_SUBHEADING')}</p>
        </div>
      </div>
      <div className="ForgotCustomerIDContainer">
        <div className={'ForgotCustomerIDForm'}>
          <form>
            <Grid container flexDirection="column" spacing={4}>
              {formFields.map((i, index) => {
                return (
                  !i.isHidden && (
                    <Grid item key={i.id}>
                      <div className={'form-field-container'}>
                        <InputWidget
                          required={i.required}
                          type={i.type}
                          id={i.id}
                          label={t(i.label)}
                          value={i.value}
                          setValue={i.setValue}
                          error={i.error}
                          helperText={i.helperText}
                        />
                        {i.label === 'EMAIL_ADDRESS' && (
                          <Button
                            id="verifyEmail"
                            variant="text"
                            disabled={verifyEmailDisabled || otpSent}
                            onClick={() => sendOtptoUser()}
                            startIcon={
                              verifyLoader && (
                                <CircularProgress
                                  size="small"
                                  style={{color: '#000'}}
                                />
                              )
                            }
                          >
                            {t('VERIFY_EMAIL')}
                          </Button>
                        )}
                        {otpSent && i.label === 'EMAIL_ADDRESS' && (
                          <div className="verifyEmail">
                            <CardContent className="verifyEmailCard">
                              <label>
                                {t('CODE_SENT_TO_EMAIL', {email: email})}
                              </label>
                              <TextField
                                helperText={t('CODE_HELPER_TEXT')}
                                label={t('VERIFICATION_CODE')}
                                type="text"
                                data-testid="customerid-email-otp"
                                required={false}
                                value={otpCode}
                                size="small"
                                onChange={(e) => {
                                  if (e.target.value.length <= 6) {
                                    otpChangeHandler(e.target.value);
                                  }
                                }}
                              />
                            </CardContent>
                            <Button
                              id="resendOtp"
                              variant="text"
                              disabled={verifyEmailDisabled}
                              onClick={() => sendOtptoUser()}
                              startIcon={
                                verifyLoader && (
                                  <CircularProgress
                                    size="small"
                                    style={{color: '#000'}}
                                  />
                                )
                              }
                            >
                              {t('RESEND_CODE')}
                            </Button>
                          </div>
                        )}
                      </div>
                    </Grid>
                  )
                );
              })}
              <div className="ForgotCustomerIDAction">
                <Grid item>
                  <Button
                    onClick={handleSubmit}
                    type="button"
                    variant="primary"
                    id="continueButton"
                    disabled={
                      submitDisabled ||
                      formFields.some(
                        (field) => !field.isHidden && field.value.length <= 0
                      ) ||
                      otpCode.length !== 6 ||
                      contract.length !==
                        COUNTRY_CONTRACT_LENGTH[selectedCountry]
                    }
                    startIcon={
                      loader && (
                        <CircularProgress
                          size="small"
                          style={{color: '#000'}}
                        />
                      )
                    }
                  >
                    {t('SUBMIT')}
                  </Button>
                  <Button
                    onClick={forgotUniqueID}
                    type="button"
                    variant="secondary"
                    id="cancelButton"
                  >
                    {t('BACK')}
                  </Button>
                </Grid>
              </div>
            </Grid>
          </form>
        </div>
      </div>
    </div>
  );
};
export default ForgotCustomerID;
